import { Person } from '@eformulieren/model'
import type { Language } from '@eformulieren/engine'
import { NWO_EMPLOYEE_LABEL, NWO_EMPLOYEE, isNwoEmployee } from '../document/presence.js'
import { inLanguage } from '../language/text.js'
import { colorFor } from './attribution.js'
import type { ColorPair } from './ychange.js'

/**
 * Een deelnemer van de interne kant zoals hij in beeld komt op een document dat de externe kant
 * kan lezen: "Medewerker van NWO" (R-66a, R-139, B6, opdracht 8).
 *
 * De pod stuurt een merkteken en geen tekst ({@link MEDEWERKER_VAN_NWO}); wat hier staat is wat de
 * lezer ervan maakt, in de presence-balk en bij de cursor.
 */

/**
 * De kleur van een medewerker van NWO: voor iedereen dezelfde, zodat ze niets over de persoon zegt.
 *
 * Uit het auteurspalet en niet een eigen grijs, omdat dat palet op contrast is gemeten voor precies
 * deze twee toepassingen, de stip en het streepje (WCAG 1.4.11) en de tint onder het label
 * (`attributie.ts`). Dat de kleur met een gewone deelnemer kan samenvallen is geen bezwaar: het
 * label staat er altijd als tekst bij, en kleur is nooit de enige drager (R-139).
 */
export const NWO_EMPLOYEE_COLOR: ColorPair = {
  light: 'var(--ef-auteur-1-licht)',
  dark: 'var(--ef-auteur-1-donker)',
}

/** Het label bij het merkteken, in de taal van wie kijkt. */
export function nwoEmployeeLabel(language: Language): string {
  return inLanguage(NWO_EMPLOYEE_LABEL, language)
}

/**
 * Wat de cursor-extensie als `user` in de awareness zet.
 *
 * De extensie overschrijft wat de pagina erin zette, dus dit is het tweede slot op dezelfde plek:
 * wie als medewerker van NWO meedoet, zet ook hier het merkteken en geen naam of kleur die uit
 * zijn persoon komt.
 */
export function caretUser(name: string, anonymous: boolean): Record<string, string> {
  if (anonymous) return { ...NWO_EMPLOYEE }
  const colorPair = colorFor(name)
  return { name: name, color: colorPair.dark, tint: colorPair.light }
}

/**
 * De cursor van een medeschrijver: een streepje met een label (R-72).
 *
 * Twee varianten en niet één, want de cursor bestaat uit twee dingen. Het palet heeft per auteur
 * een donkere en een lichte variant: de donkere is de merkkleur zelf, de lichte is die kleur op 15
 * procent dekking (M4.11b). Het streepje moet zichtbaar zijn en krijgt dus de donkere; het label
 * draagt tekst en krijgt de lichte, met de gewone tekstkleur erop. De standaardweergave van de
 * extensie maakt dat onderscheid niet: die zet één kleur inline op zowel de rand als de
 * labelachtergrond, en een van de twee is dan fout. De naam gaat mee, want kleur is nooit de enige
 * drager (R-139).
 *
 * Bij het merkteken het label in de taal van wie kijkt en de vaste kleur.
 */
export function characterCaret(
  user: Record<string, unknown>,
  language: Language,
  nameOf?: (person: string) => string | undefined,
): HTMLElement {
  const anonymous = isNwoEmployee(user)
  const separator = document.createElement('span')
  separator.classList.add('collaboration-carets__caret')
  separator.style.borderColor = anonymous ? NWO_EMPLOYEE_COLOR.dark : String(user.color ?? '')
  const label = document.createElement('div')
  label.classList.add('collaboration-carets__label')
  label.style.backgroundColor = anonymous
    ? NWO_EMPLOYEE_COLOR.light
    : String(user.tint ?? user.color ?? '')
  label.textContent = caretLabelText(user, language, nameOf)
  separator.append(label)
  return separator
}

/**
 * De selectie van een medeschrijver: de lichte tint van zijn kleur onder de tekst (R-72).
 *
 * Een eigen weergave en niet die van de extensie. Die zet `background-color: ${color}70`, dus de
 * kleur met een alfa erachter, en dat werkt alleen voor een hexwaarde. Onze kleur is een token
 * (`var(--ef-auteur-N-donker)`), en `var(...)70` is geen geldige CSS: de selectie van een ander
 * was daardoor onzichtbaar. Bij het merkteken zet y-tiptap zelf een oranje hexkleur in de plaats
 * van de ontbrekende, en die hoort niet bij het palet. De lichte variant is de kleur op 15 procent
 * dekking (M4.11b), dus de tekst blijft leesbaar.
 */
export function selectionOf(user: Record<string, unknown>): { class: string; style: string } {
  const tint = isNwoEmployee(user) ? NWO_EMPLOYEE_COLOR.light : String(user.tint ?? '')
  return {
    class: 'collaboration-carets__selection',
    style: tint === '' ? '' : `background-color: ${tint}`,
  }
}

/**
 * De naam bij de cursor: dezelfde als bij de sectie en het veld, want het is dezelfde `nameOf`
 * (`presenceName` in `FillForm`). Nooit een persoonssleutel (zie `presentName` in `PresenceBar.tsx`).
 */
export function caretLabelText(
  user: Record<string, unknown>,
  language: Language,
  nameOf?: (person: string) => string | undefined,
): string {
  if (isNwoEmployee(user)) return nwoEmployeeLabel(language)
  const name = String(user.name ?? '')
  return Person.safeParse(name).success ? (nameOf?.(name) ?? (language === 'nl' ? 'Deelnemer' : 'Participant')) : name
}
